import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { trackEvent } from '../../utils/Analytics';
import { useTranslation } from '../hooks/useTranslation';
import { ExampleReportCTA } from './ExampleReportCTA';
import config from '../config';
import { fetchWithLanguage } from '../utils/api';
import { ProcessingAnimation } from './ProcessingAnimation';

// Add supported file types configuration
const SUPPORTED_FILE_TYPES = {
    images: {
        accept: 'image/jpeg,image/png',
        description: '📸 Images (JPEG, PNG)'
    },
    documents: {
        accept: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.google-apps.document',
            'application/rtf',
            'text/plain',
            'text/markdown',
            'text/x-markdown',
            'application/x-iwork-pages-sffpages',
            'application/vnd.oasis.opendocument.text'
        ].join(','),
        description: '📄 Documents (PDF, DOC, DOCX, RTF, TXT, MD)'
    }
};

export const FileUploader = forwardRef(({ onFileUpload, isError, hasConsent, isProcessing }, ref) => {
    const fileInputRef = useRef(null);
    const { t, ready } = useTranslation();
    const [isExampleLoading, setIsExampleLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        openFileBrowser: () => {
            setTimeout(() => {
                fileInputRef.current?.click();
            }, 100);
        }
    }));

    const handleClick = () => {
        // Only trigger consent check if we don't have consent
        if (!hasConsent) {
            onFileUpload();
            return;
        }
        // If we have consent, open file browser directly
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            trackEvent('Lab Results', 'Upload', file.type, file.size);
            onFileUpload(file);
            // Reset the input value to allow uploading the same file again
            event.target.value = '';
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0];
        if (file) {
            onFileUpload(file);
            trackEvent('Lab Results', 'Drop Upload', file.type, file.size);
        }
    };

    const handleExampleLoad = async () => {
        try {
            setIsExampleLoading(true);
            trackEvent('Lab Results', 'Load Example', 'example-report');

            const response = await fetchWithLanguage(`${config.apiUrl}/example-report`);
            if (!response.ok) {
                throw new Error('Failed to load example report');
            }

            const data = await response.json();
            if (data?.success && data.results) {
                // Pass a flag to indicate this is an example report
                onFileUpload(null, data.results);
                trackEvent('Lab Results', 'Example Success');
            } else {
                throw new Error(data.error || 'Failed to process example report');
            }
        } catch (error) {
            console.error('Error loading example:', error);
            trackEvent('Lab Results', 'Example Error', error.message);
            onFileUpload(null, null, error.message);
        } finally {
            setIsExampleLoading(false);
        }
    };

    const results = {
        labData: {
            glucose: "95 mg/dL",
            cholesterol: "180 mg/dL",
            // ... other lab results
        },
        analysis: {
            summary: "All values within normal range",
            recommendations: ["Maintain current diet", "Continue exercise routine"],
            // ... other analysis data
        }
    };

    // Update the input accept attribute to include all supported types
    const acceptedTypes = Object.values(SUPPORTED_FILE_TYPES)
        .map(type => type.accept)
        .join(',');

    return (
        <div className="space-y-4">
            {ready ? (
                <>
                    <div
                        onClick={handleClick}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className={`relative rounded-xl border ${isError
                            ? 'border-orange-300 bg-orange-50 hover:bg-orange-100'
                            : 'border-indigo-300 bg-indigo-100 hover:bg-indigo-200'
                            } transition-all duration-200 shadow-sm hover:shadow-lg p-8 text-center cursor-pointer 
                        active:scale-[0.98] hover:-translate-y-0.5 transform`}
                    >
                        <div className="space-y-6">
                            {/* Title Section */}
                            <h2 className="text-lg font-medium text-orange-600 mb-3 leading-relaxed">
                                {t('upload.title')}
                            </h2>
                            <div className="flex justify-center space-x-6">
                                <svg
                                    className="w-14 h-14 text-indigo-500 transition-transform duration-300 hover:scale-110 hover:rotate-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                    />
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                </svg>
                                <svg
                                    className="w-14 h-14 text-indigo-500 transition-transform duration-300 hover:scale-110 hover:-rotate-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                </svg>
                            </div>

                            {/* Add processing animation here, between icons and text */}
                            {isProcessing && (
                                <div className="py-2">
                                    <ProcessingAnimation />
                                </div>
                            )}

                            {/* Text Section */}
                            <div>
                                <div className="text-base text-gray-700 space-y-2 text-left max-w-sm mx-auto transition-all duration-200 hover:text-indigo-900">
                                    <p>
                                        <span className="mr-2">📸</span>
                                        {t('upload.camera')}
                                    </p>
                                    <p>
                                        <span className="mr-2">📄</span>
                                        {t('upload.dropFiles')}
                                    </p>
                                    <p>
                                        <span className="mr-2">📁</span>
                                        {t('upload.browse')}
                                    </p>
                                    <div className="mt-4 text-sm text-gray-600">
                                        <p className="font-medium mb-1">{t('upload.supportedFormats')}:</p>
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>{t('upload.fileTypes.images')}</li>
                                            <li>{t('upload.fileTypes.documents')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Input */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                onChange={handleFileChange}
                                accept={acceptedTypes}
                            />

                            {/* Privacy notice with updated styling */}
                            <div className="mt-8 px-4">
                                <p className="text-sm text-indigo-700 font-medium py-1">
                                    🔒 {t('upload.privacyNotice')}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">or</span>
                        </div>
                    </div>

                    <ExampleReportCTA
                        onExampleLoad={handleExampleLoad}
                        isLoading={isExampleLoading}
                    />
                </>
            ) : (
                <div>Loading...</div>
            )}
        </div>
    );
}); 